import React, { useMemo } from "react";
import {
	CalculateTotalsResult,
	getJobItemsForPdfTable,
} from "@/dentlab/src/lib/utils/calculate";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import {
	JobDocumentEntityType,
	JobItemEntityType,
	OrganizationEntityType,
} from "@/lib/supabase/supabaseTypes";
import { PdfTableTotalsSection } from "../pdf-table-totals-section.component";
import {
	ArticleFields,
	PdfTemplateConfiguration,
} from "../../../context/PdfTemplateConfigurationContext";
import { PdfTemplateType } from "../../document-template.types";
import { allItemsHaveSameTp } from "../pdf-job-information-utils";

export type PdfTableColumns = {
	field: string;
	label: string;
	align: "left" | "right";
	width: number;
};

/**
 *
 * @param showDrTax - If the document is a Lieferschein or Gutschrift job items with ZAZ-Steuer (Dr. Tax) should be marked
 * @returns
 */
export const PdfJobItemsTable: React.FC<{
	jobDocument: JobDocumentEntityType;
	jobItems: JobItemEntityType[];
	organization: OrganizationEntityType;
	showDrTax: boolean;
	jobTotals?: CalculateTotalsResult;
	excludePrices?: boolean;
	pdfTemplateConfiguration: PdfTemplateConfiguration;
	options?: {
		noteColumn?: boolean;
	};
	isMwstEust: boolean;
	templateType?: PdfTemplateType;
	currency?: string | null;
}> = ({
	jobDocument,
	jobItems,
	organization,
	showDrTax,
	excludePrices = false,
	jobTotals,
	pdfTemplateConfiguration,
	options = { noteColumn: false },
	isMwstEust,
	templateType,
	currency,
}) => {
	const jobItemsForTable = getJobItemsForPdfTable(jobItems);
	let numberOfColumns = 0;
	let numberOfAdditionalColumns = 0;

	// 1) Build initial column definitions (without width)
	const baseColumns: PdfTableColumns[] = useMemo(() => {
		const cols: PdfTableColumns[] = [];
		const add = (field: string, label: string, align: "left" | "right") =>
			cols.push({ field, label, align, width: 0 });
		add("code", "Code", "left");
		add("quantity", "Menge", "left");
		add("description", "Text", "left");
		numberOfColumns = 3;
		if (
			options.noteColumn &&
			pdfTemplateConfiguration[PdfTemplateType.LIEFERSCHEIN]
				.showNotesInTable &&
			jobItems.some((i) => i.note)
		) {
			add("note", "Notiz", "left");
			numberOfColumns++;
		}
		const af: ArticleFields | undefined = templateType
			? (pdfTemplateConfiguration[templateType] as any)?.articleFields
			: undefined;
		if (af?.showAdditionalText && jobItems.some((i) => i.additional_text)) {
			add("additional_text", "Zusatztext", "left");
			numberOfColumns++;
		}
		if (af?.showManufacturer && jobItems.some((i) => i.manufacturer)) {
			add("manufacturer", "Hersteller", "left");
			numberOfColumns++;
		}
		if (af?.showCeNumber && jobItems.some((i) => i.ce_number)) {
			add("ce_number", "CE-Nr.", "left");
			numberOfColumns++;
		}
		if (af?.showComposition && jobItems.some((i) => i.composition)) {
			add("composition", "Zusammensetzung", "left");
			numberOfColumns++;
		}
		if (af?.showLotNumber && jobItems.some((i) => i.lot)) {
			add("lot", "Lot Nr.", "left");
			numberOfColumns++;
		}
		if (!allItemsHaveSameTp(jobItems)) {
			add("tp_value", "TP-Wert", "left");
			numberOfColumns++;
		}
		if (!excludePrices) {
			add("price", "Preis/E", "right");
			numberOfColumns++;
		}
		if (jobItems.some((i) => i.discount) && !excludePrices) {
			add("discount", "Rabatt", "right");
			numberOfColumns++;
		}
		if (!excludePrices) {
			add("total", "Betrag", "right");
			numberOfColumns++;
		}
		if (showDrTax && jobItems.some((i) => i.is_req_dr_tax)) {
			add("is_req_dr_tax", "", "right");
			numberOfAdditionalColumns++;
		}
		if (jobItems.some((i) => i.is_warranty)) {
			add("is_warranty", "", "right");
			numberOfAdditionalColumns++;
		}
		return cols;
	}, [jobItems, excludePrices, showDrTax, options, pdfTemplateConfiguration]);

	// 2) Compute column widths
	const colsWithWidth = useMemo(() => {
		const activeArticleCols = [
			"additional_text",
			"manufacturer",
			"ce_number",
			"composition",
			"lot",
		].filter((f) => baseColumns.some((c) => c.field === f)).length;

		const articleWidths: Record<string, number> = {
			additional_text: activeArticleCols >= 4 ? 8 : 12,
			manufacturer: activeArticleCols >= 4 ? 8 : 10,
			ce_number: activeArticleCols >= 4 ? 6 : 8,
			composition: activeArticleCols >= 4 ? 8 : 12,
			lot: activeArticleCols >= 4 ? 6 : 8,
		};
		const totalArticleWidth = baseColumns
			.filter((c) => articleWidths[c.field] !== undefined)
			.reduce((sum, c) => sum + articleWidths[c.field], 0);

		const PREFERRED: Record<string, number> = {
			code: 7,
			quantity: 5,
			description: Math.max(15, 30 - totalArticleWidth * 0.6),
			note: 10,
			...articleWidths,
			tp_value: 6,
			price: 8,
			discount: 7,
			total: 9,
			is_req_dr_tax: 2,
			is_warranty: 5,
		};

		const rawTotal = baseColumns.reduce(
			(sum, col) => sum + (PREFERRED[col.field] ?? 5),
			0
		);
		const scale = 100 / rawTotal;

		const columns = baseColumns.map((col) => ({
			...col,
			width: +((PREFERRED[col.field] ?? 5) * scale).toFixed(2),
		}));

		return columns;
	}, [baseColumns]);

	// 4) Render table
	const fontSizeStyling = {
		sm: "6pt",
		md: "7pt",
		lg: "9pt",
	}[pdfTemplateConfiguration.general.size ?? "lg"];

	const tableBackgroundColor =
		pdfTemplateConfiguration.general.tableBackgroundColor || "#d3d3d3";

	const getRowHeight = () => {
		const density = pdfTemplateConfiguration.general.tableDensity ?? "md";
		return {
			sm: "2mm",
			md: "5mm",
			lg: "8mm",
		}[density];
	};

	const rowHeight = getRowHeight();
	const cellPadding =
		pdfTemplateConfiguration.general.tableDensity === "sm"
			? "0.5pt"
			: "2pt";

	return (
		<table
			style={{
				width: "100%",
				borderCollapse: "collapse",
				fontSize: fontSizeStyling,
				backgroundColor: "white",
			}}
		>
			<thead>
				<tr
					style={{
						backgroundColor: tableBackgroundColor,
						borderBottom: "1px solid black",
						height: rowHeight,
					}}
				>
					{colsWithWidth.map((column: PdfTableColumns) => (
						<th
							key={column.field}
							style={{
								textAlign: column.align,
								width: `${column.width}%`,
								verticalAlign: "top",
								padding: cellPadding,
								hyphens: "auto",
							}}
						>
							{column.label}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{jobItemsForTable.map((row) => (
					<tr key={row.code_e + row.id} style={{ height: rowHeight }}>
						{colsWithWidth.map((column: PdfTableColumns) => {
							if (column.field === "is_req_dr_tax") {
								return (
									<td
										key={column.field}
										style={{
											textAlign: "center",
											width: `${column.width}%`,
											verticalAlign: "top",
											padding: cellPadding,
										}}
									>
										{row[column.field] ? "*" : ""}
									</td>
								);
							}
							if (column.field === "is_warranty") {
								return (
									<td
										key={column.field}
										style={{
											textAlign: column.align,
											width: `${column.width}%`,
											verticalAlign: "top",
											padding: cellPadding,
										}}
									>
										{row.is_warranty ? "gratis" : ""}
									</td>
								);
							}
							if (
								column.field === "code" ||
								column.field === "lot" ||
								column.field === "manufacturer" ||
								column.field === "ce_number"
							) {
								return (
									<td
										key={column.field}
										style={{
											textAlign: column.align,
											width: `${column.width}%`,
											verticalAlign: "top",
											padding: cellPadding,
											whiteSpace: "pre-wrap", // Support multiple lines
											wordBreak: "break-all", // Break at any character for codes/lots
										}}
									>
										{(row as any)[column.field]}
									</td>
								);
							}
							if (
								column.field === "description" ||
								column.field === "note" ||
								column.field === "additional_text" ||
								column.field === "composition"
							) {
								return (
									<td
										key={column.field}
										style={{
											textAlign: column.align,
											width: `${column.width}%`,
											verticalAlign: "top",
											padding: cellPadding,
											whiteSpace: "pre-wrap", // Support multiple lines
											wordBreak: "break-word", // Allow breaking long words
										}}
									>
										{(row as any)[column.field]}
									</td>
								);
							}
							return (
								<td
									key={column.field}
									style={{
										textAlign: column.align,
										width: `${column.width}%`,
										verticalAlign: "top",
										padding: cellPadding,
									}}
								>
									{row.type ===
										JobItemTypeEnum.ARTICLE_DISCOUNTS &&
										(column.field === "price" ||
											column.field === "total") &&
										"-"}
									{(row as any)[column.field]}
								</td>
							);
						})}
					</tr>
				))}
			</tbody>
			{jobTotals && (
				<PdfTableTotalsSection
					totals={jobTotals}
					jobDocument={jobDocument}
					organization={organization}
					numberOfColumns={numberOfColumns}
					numberOfAdditionalColumns={numberOfAdditionalColumns}
					pdfTemplateConfiguration={pdfTemplateConfiguration}
					isMwstEust={isMwstEust}
					templateType={templateType}
					currency={currency}
				/>
			)}
		</table>
	);
};
